import React from 'react'
import { ChartThemeManager, type ChartTheme } from '../Services/ChartThemeManager'

export interface DragOverlayProps {
  /** Whether the overlay is visible */
  isVisible: boolean
  
  /** Visual theme for the overlay */
  theme?: ChartTheme
  
  /** Text to display in the overlay */
  text?: string
  
  /** Icon to display in the overlay */
  icon?: React.ReactNode
  
  /** Whether to show the animated pulse effect */
  showPulse?: boolean
  
  /** Additional CSS styles */
  style?: React.CSSProperties
  
  /** Custom CSS class name */
  className?: string
}

/**
 * Overlay component shown during drag-and-drop operations
 * Provides visual feedback when dragging curves over chart areas
 */
export function DragOverlay({
  isVisible,
  theme = 'dark',
  text = 'Drop here to add curve',
  icon,
  showPulse = true,
  style,
  className
}: DragOverlayProps) {
  if (!isVisible) return null

  const baseStyles = ChartThemeManager.createDragOverlayStyles(theme)
  const colors = ChartThemeManager.getChartColors(theme)
  
  const combinedStyles = {
    ...baseStyles,
    ...style
  }

  const chipStyles: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: colors.dragOverlay.chipBackground,
    color: colors.dragOverlay.chipText,
    border: `1px solid ${colors.dragOverlay.chipBorder}`,
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    animation: showPulse ? 'dragPulse 1.5s ease-in-out infinite' : undefined
  }

  const defaultIcon = (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity: 0.8 }}
    >
      {/* Arrow down icon */}
      <path d="M12 5v14" />
      <path d="M19 12l-7 7-7-7" />
    </svg>
  )

  return (
    <>
      {/* CSS keyframes for pulse animation */}
      <style>
        {`
          @keyframes dragPulse {
            0%, 100% { transform: scale(1); opacity: 0.9; }
            50% { transform: scale(1.05); opacity: 1; }
          }
        `}
      </style>
      
      <div
        className={className}
        style={combinedStyles}
        data-testid="drag-overlay"
      >
        <div style={chipStyles} data-testid="drag-overlay-chip">
          {icon || defaultIcon}
          <span>{text}</span>
        </div>
      </div>
    </>
  )
}

export default DragOverlay 